import { useState, useEffect } from 'react';
import { MapPin, Loader2, Info, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import { RequireProducerCapability } from '@/components/producer/RequireProducerCapability';

const EXAMPLE_TEXT = `Beställningar hämtas på gården fredagar kl 16–18.\nHör gärna av dig om du behöver en annan tid.`;

export default function ProducerShippingPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { toast } = useToast();

  const [instructions, setInstructions] = useState('');
  const [savedInstructions, setSavedInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!farmId) return;
    setLoading(true);
    supabase
      .from('producers')
      .select('pickup_instructions')
      .eq('id', farmId)
      .single()
      .then(({ data }) => {
        const val = data?.pickup_instructions ?? '';
        setInstructions(val);
        setSavedInstructions(val);
        setLoading(false);
      });
  }, [farmId]);

  const isDirty = instructions !== savedInstructions;

  const handleSave = async () => {
    if (!farmId) return;
    if ((await import('@/lib/supportPreview')).isSupportPreviewActive()) return;
    setSaving(true);
    const { error } = await supabase
      .from('producers')
      .update({ pickup_instructions: instructions.trim() || null })
      .eq('id', farmId);

    setSaving(false);

    if (error) {
      toast({ title: 'Kunde inte spara', description: error.message, variant: 'destructive' });
      return;
    }

    setSavedInstructions(instructions.trim());
    toast({ title: 'Uppdaterat' });
    track('producer_pickup_instructions_updated', { producer_id: farmId });
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 py-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-52 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <RequireProducerCapability capability="canManageShop" producerId={farmId}>
      <div className="max-w-3xl mx-auto space-y-5 py-2">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Frakt & leverans</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Så hämtar kunder sina beställningar.
          </p>
        </div>

        {/* Pickup card */}
        <Card className="border border-border/60 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                <MapPin className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[15px] leading-tight">Hämtas på plats</span>
                  <Badge variant="secondary" className="text-[11px] px-2 py-0 font-normal shrink-0">
                    Standard
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Kunder hämtar sina beställningar hos dig.
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 pt-0">
            {/* Textarea section */}
            <div className="space-y-2">
              <Label htmlFor="pickup-instructions" className="text-sm font-medium">
                Utlämningsinformation
              </Label>
              <p className="text-xs text-muted-foreground" id="pickup-instructions-desc">
                Beskriv när och hur kunder kan hämta. Tider, plats och ev. kontaktinfo.
              </p>
              <Textarea
                id="pickup-instructions"
                aria-describedby="pickup-instructions-desc"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="T.ex. Beställningar hämtas på gården fredagar kl 16–18."
                className="min-h-[140px] resize-y"
                rows={5}
              />
              {/* Example fill button */}
              {!instructions.trim() && (
                <button
                  type="button"
                  onClick={() => setInstructions(EXAMPLE_TEXT)}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Sparkles className="h-3 w-3" />
                  Fyll i exempel
                </button>
              )}
            </div>

            {/* Preview hint */}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Det här visas för kunder i förbeställningsdialogen.</span>
            </div>
          </CardContent>

          <CardFooter className="border-t pt-4 flex justify-end">
            <Button onClick={handleSave} disabled={saving || !isDirty} size="sm">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Spara
            </Button>
          </CardFooter>
        </Card>

        {/* Mobile sticky save */}
        {isDirty && (
          <div className="fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur-sm p-3 md:hidden">
            <Button className="w-full" onClick={handleSave} disabled={saving} size="sm">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Spara
            </Button>
          </div>
        )}
      </div>
    </RequireProducerCapability>
  );
}
